import { Button } from "@/design-system/Button/Button";
import Input from "@/design-system/Input/Input";
import { cn } from "@/lib/utils";
import { Search } from "lucide-react";
import { useState } from "react";

/**
 * The toolbar search's open state. `collapsible` (phones): a button until
 * tapped, open while it has a term. `takesOver`: the field has the band, so
 * the host hides what sits beside it.
 */
export const useExpandableSearch = (value: string, collapsible: boolean) => {
  const [expanded, setExpanded] = useState(false);
  const open = !collapsible || expanded || value.length > 0;
  return { open, takesOver: collapsible && open, setExpanded };
};

/** A toolbar's search: always a field on desktop, a search button on phones. */
export const ExpandableSearch = ({
  search,
  value,
  onChange,
  placeholder,
  className,
}: {
  search: ReturnType<typeof useExpandableSearch>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** On the field's wrapper, e.g. a min width beside other controls. */
  className?: string;
}) => {
  const close = () => {
    onChange("");
    search.setExpanded(false);
  };
  return search.open ? (
    <div className={cn("w-54 max-touch-device:w-full", className)}>
      <Input
        type="search"
        size="small"
        icon={<Search className="size-4" />}
        placeholder={placeholder}
        autoFocus={search.takesOver}
        value={value}
        onChange={onChange}
        onClear={close}
        onBlur={() => {
          if (value.length === 0) search.setExpanded(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            close();
            e.currentTarget.blur();
          }
        }}
      />
    </div>
  ) : (
    <Button
      variant="secondary"
      size="small"
      icon={<Search className="size-4" />}
      aria-label={placeholder}
      onClick={() => search.setExpanded(true)}
    />
  );
};
